import {
  createProduct,
  deletePackage,
  deleteProduct,
  fetchEvents,
  fetchPackageRates,
  fetchPackages,
  fetchProducts,
  fetchSeasons,
  savePackage,
  updateProduct,
} from "../lib/api.js";
import { sum } from "../lib/aggregate.js";
import { formatCurrency, parseAmount } from "../lib/format.js";
import { PER_OPTIONS, linesTotal, needsQuote } from "../lib/calc.js";
import { costLineRowHtml, readCostLines } from "./eventForm.js";
import { confirmDialog, escapeHtml, formatAmountsIn, openModal, showError, wireAmountInputs } from "../lib/ui.js";
import { fetchColoniaFlag } from "../lib/coloniaApi.js";

export async function renderProducts(container) {
  container.innerHTML = `<h2>Productos y paquetes</h2><p>Cargando…</p>`;

  try {
    const [products, events, packages, packageRates, seasons, coloniaNueva] = await Promise.all([
      fetchProducts(),
      fetchEvents(),
      fetchPackages(),
      fetchPackageRates(),
      fetchSeasons(),
      fetchColoniaFlag(),
    ]);
    const reload = () => renderProducts(container);

    const stats = products.map((p) => {
      const productEvents = events.filter((e) => e.product_id === p.id);
      return {
        ...p,
        count: productEvents.length,
        seasonCount: seasons.filter((s) => s.product_id === p.id).length,
        income: sum(productEvents, (e) => e.income),
        profit: sum(productEvents, (e) => e.profit),
        packages: packages
          .filter((pkg) => pkg.product_id === p.id)
          .map((pkg) => ({ ...pkg, rates: packageRates.find((r) => r.package_id === pkg.id) ?? null })),
      };
    });

    // Con la Colonia nueva prendida, el producto de temporada (el que tiene
    // temporadas y ni eventos ni paquetes) se administra en Colonia: se oculta
    // sólo en pantalla. La tabla de productos no se toca (§6.8).
    const visibles = coloniaNueva
      ? stats.filter((p) => !(p.seasonCount > 0 && p.count === 0 && p.packages.length === 0))
      : stats;
    const ocultos = stats.length - visibles.length;

    container.innerHTML = `
      <div id="view">
      <h2>Productos y paquetes</h2>
      ${
        ocultos > 0
          ? '<div class="notice"><span>La colonia se administra en <a href="#/colonia">Colonia →</a></span></div>'
          : ""
      }
      <div class="toolbar">
        <span>${visibles.length} producto(s)</span>
        <button class="btn" id="new-product">+ Nuevo producto</button>
      </div>
      <div class="cards-grid">
        ${
          visibles.length === 0
            ? "<p>Todavía no hay productos.</p>"
            : visibles
                .map(
                  (p) => `
          <div class="card${p.active ? "" : " row-inactive"}">
            <div style="display:flex; flex-wrap:wrap; justify-content:space-between; align-items:center; gap:8px; margin-bottom:10px">
              <span class="badge" style="background:${escapeHtml(p.color)}">${escapeHtml(p.name)}</span>
              <span style="display:flex; gap:6px">
                <button class="icon-btn" data-edit="${escapeHtml(p.id)}">Editar</button>
                <button class="icon-btn danger" data-delete="${escapeHtml(p.id)}">Borrar</button>
              </span>
            </div>
            <div class="hint" style="margin:-4px 0 10px">
              ${p.kind === "temporada" ? "Temporada" : "Evento"}${p.active ? "" : " · sin uso"}${
                    p.form_schema?.length ? ` · ${p.form_schema.length} campo(s) propio(s)` : ""
                  }
            </div>
            <div class="label">Eventos</div>
            <div class="value" style="font-size:20px">${p.count}</div>
            <div class="label" style="margin-top:10px">Ingresos totales</div>
            <div class="value" style="font-size:18px">${formatCurrency(p.income)}</div>
            <div class="label" style="margin-top:10px">Ganancia neta</div>
            <div class="value ${p.profit >= 0 ? "positive" : "negative"}" style="font-size:18px">${formatCurrency(p.profit)}</div>

            <div class="package-list">
              <div class="label" style="margin-top:14px">Paquetes</div>
              ${
                p.packages.length === 0
                  ? '<p class="hint" style="margin:4px 0">Sin paquetes. Un paquete guarda las tarifas ya cargadas para no repetirlas en cada evento.</p>'
                  : p.packages.map((pkg) => packageBlockHtml(pkg)).join("")
              }
              <button class="icon-btn" data-new-package="${escapeHtml(p.id)}">+ Paquete</button>
            </div>
          </div>`
                )
                .join("")
        }
      </div>
      </div>
    `;

    const view = container.querySelector("#view");

    view.querySelector("#new-product").addEventListener("click", () => openProductModal(null, reload));

    view.addEventListener("click", async (e) => {
      const deleteId = e.target.dataset?.delete;
      const editId = e.target.dataset?.edit;
      const newPackageFor = e.target.dataset?.newPackage;
      const editPackageId = e.target.dataset?.editPackage;

      if (editId) {
        openProductModal(
          stats.find((p) => p.id === editId),
          reload
        );
      }

      if (deleteId) {
        const product = stats.find((p) => p.id === deleteId);
        if (confirmDialog(deleteWarning(product))) {
          await deleteProduct(deleteId);
          reload();
        }
      }

      if (newPackageFor) {
        openPackageModal(newPackageFor, null, null, reload);
      }

      if (editPackageId) {
        const pkg = packages.find((p) => p.id === editPackageId);
        const rates = packageRates.find((r) => r.package_id === editPackageId) ?? null;
        openPackageModal(pkg.product_id, pkg, rates, reload);
      }
    });
  } catch (error) {
    showError(container, error);
  }
}

/**
 * De qué está hecho un paquete, a la vista en la tarjeta del producto: hasta
 * ahora sólo se veía el precio y había que abrir Editar para saber qué costos
 * tenía cargados adentro.
 */
const PER_LABEL = Object.fromEntries(PER_OPTIONS.map(([per, label]) => [per, label]));

function packageBlockHtml(pkg) {
  const r = pkg.rates;
  const lines = Array.isArray(r?.cost_lines) ? r.cost_lines : [];

  if (!r || lines.length === 0) {
    return `<div class="package-block">
      <div class="package-row">
        <span class="package-name">${escapeHtml(pkg.name)}</span>
        <button class="icon-btn" data-edit-package="${escapeHtml(pkg.id)}">Editar</button>
      </div>
      <p class="hint">Sin costos cargados. Hasta que los tenga, los eventos de este paquete calculan cero.</p>
    </div>`;
  }

  const prices = Array.isArray(r.price_lines) ? r.price_lines : [];
  const teachers = Number(r.default_teachers_count) || 0;

  // Referencia a 25 chicos (lo que cubre el precio base) y a 35 (lo que pasa
  // de verdad en dos de cada tres cumples). Ver las dos juntas es lo que
  // muestra si el precio por chico extra alcanza.
  const escenario = (chicos) => {
    const counts = { children_count: chicos, teachers_count: teachers, souvenirs_count: chicos };
    const costo = linesTotal(lines, counts);
    const precio = linesTotal(prices, counts);
    return { chicos, costo, precio, ganancia: precio - costo, margen: precio > 0 ? Math.round(((precio - costo) / precio) * 100) : null };
  };
  const a25 = escenario(25);
  const a35 = escenario(35);

  const lineaHtml = (l) => `<div>
    <dt>${escapeHtml(l.concept || "Sin concepto")}${
      l.per && l.per !== "fijo" ? ` <span class="per-tag">${escapeHtml(PER_LABEL[l.per] ?? l.per)}${l.included ? ` desde ${Number(l.included) + 1}` : ""}</span>` : ""
    }${l.quote ? ' <span class="per-tag quote">a presupuestar</span>' : ""}</dt>
    <dd>${l.quote && !(Number(l.amount) > 0) ? "—" : formatCurrency(l.amount)}</dd>
  </div>`;

  return `<div class="package-block">
    <div class="package-row">
      <span class="package-name">${escapeHtml(pkg.name)}</span>
      <span class="package-price">${formatCurrency(a25.precio)}<span class="hint"> a 25</span></span>
      <button class="icon-btn" data-edit-package="${escapeHtml(pkg.id)}">Editar</button>
    </div>
    <div class="label" style="margin-top:8px">Precio</div>
    <dl class="package-rates">${prices.length ? prices.map(lineaHtml).join("") : "<div><dd class='hint'>sin líneas de precio</dd></div>"}</dl>
    <div class="label" style="margin-top:8px">Costos</div>
    <dl class="package-rates">${lines.map(lineaHtml).join("")}</dl>
    <table class="scenario-table">
      <thead><tr><th></th><th>25 chicos</th><th>35 chicos</th></tr></thead>
      <tbody>
        <tr><th>Precio</th><td>${formatCurrency(a25.precio)}</td><td>${formatCurrency(a35.precio)}</td></tr>
        <tr><th>Costo</th><td>${formatCurrency(a25.costo)}</td><td>${formatCurrency(a35.costo)}</td></tr>
        <tr><th>Ganancia</th>
          <td class="${a25.ganancia >= 0 ? "positive" : "negative"}">${formatCurrency(a25.ganancia)}${a25.margen === null ? "" : ` · ${a25.margen}%`}</td>
          <td class="${a35.ganancia >= 0 ? "positive" : "negative"}">${formatCurrency(a35.ganancia)}${a35.margen === null ? "" : ` · ${a35.margen}%`}</td>
        </tr>
      </tbody>
    </table>
    ${needsQuote(lines) ? '<p class="hint">Tiene costos a presupuestar: se cargan en cada evento.</p>' : ""}
  </div>`;
}

/**
 * Borrar un producto no arrastra lo mismo en todos los casos: los eventos
 * quedan sin producto (SET NULL), pero las temporadas se borran en cascada y
 * con ellas las inscripciones y sus pagos. El aviso lo dice según lo que
 * cuelgue de este producto en concreto.
 */
function deleteWarning(product) {
  if (product.seasonCount > 0) {
    return (
      `Este producto tiene ${product.seasonCount} temporada(s) de colonia.\n\n` +
      "Al borrarlo se borran también esas temporadas y TODAS sus inscripciones, " +
      "con lo cobrado y lo que falta cobrar. No se puede deshacer.\n\n" +
      "Si sólo querés dejar de usarlo, cancelá y desmarcá «Se puede elegir» en Editar.\n\n" +
      "¿Borrar igual?"
    );
  }
  if (product.count > 0) {
    return (
      `Este producto tiene ${product.count} evento(s). Los eventos no se borran, ` +
      "pero quedan sin producto y dejan de sumar en los reportes por producto.\n\n" +
      "Si sólo querés dejar de usarlo, cancelá y desmarcá «Se puede elegir» en Editar.\n\n" +
      "¿Borrar igual?"
    );
  }
  return "¿Borrar este producto?";
}

/** El nombre técnico del campo, que es la clave con la que se guarda en events.details. */
function slugifyFieldName(label) {
  return (
    label
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "campo"
  );
}

const FIELD_TYPES = [
  ["text", "Texto"],
  ["number", "Número"],
  ["select", "Lista de opciones"],
  ["checkbox", "Sí / no"],
];

function schemaRowHtml(field = {}) {
  const type = field.type ?? "text";
  return `
    <div class="schema-row" data-schema-row data-field-name="${escapeHtml(field.name ?? "")}">
      <div class="field">
        <label>Etiqueta</label>
        <input type="text" data-schema-label placeholder="Inflables" value="${escapeHtml(field.label ?? "")}" />
      </div>
      <div class="field">
        <label>Tipo</label>
        <select data-schema-type>
          ${FIELD_TYPES.map(
            ([value, label]) =>
              `<option value="${value}"${type === value ? " selected" : ""}>${label}</option>`
          ).join("")}
        </select>
      </div>
      <div class="field" data-schema-options-field${type === "select" ? "" : " hidden"}>
        <label>Opciones, separadas por coma</label>
        <input type="text" data-schema-options placeholder="Mañana, Tarde"
               value="${escapeHtml((field.options ?? []).join(", "))}" />
      </div>
      <button type="button" class="icon-btn danger" data-remove-row>Quitar</button>
    </div>`;
}

/**
 * Sirve para crear y para editar. Al editar se conserva el `name` original de
 * cada campo propio: es la clave con la que ya quedaron guardados los datos en
 * events.details, así que renombrar la etiqueta no puede cambiarla sin dejar
 * huérfano lo ya cargado.
 */
function openProductModal(product, onSaved) {
  const editing = Boolean(product);
  const schema = Array.isArray(product?.form_schema) ? product.form_schema : [];

  const modal = openModal(`
    <button class="modal-close" type="button" data-close>✕</button>
    <h3>${editing ? "Editar producto" : "Nuevo producto"}</h3>
    <form id="product-form">
      <div class="form-grid">
        <div class="field">
          <label for="p-name">Nombre</label>
          <input type="text" id="p-name" name="name" required value="${escapeHtml(product?.name ?? "")}" />
        </div>
        <div class="field">
          <label for="p-color">Color</label>
          <input type="color" id="p-color" name="color" value="${escapeHtml(product?.color ?? "#4f6ef7")}" />
        </div>
        <div class="field">
          <label for="p-kind">Tipo</label>
          <select id="p-kind" name="kind">
            <option value="evento"${product?.kind === "temporada" ? "" : " selected"}>Evento suelto</option>
            <option value="temporada"${product?.kind === "temporada" ? " selected" : ""}>Temporada (colonia)</option>
          </select>
          <p class="hint">Las temporadas se administran desde Colonia.</p>
        </div>
        <div class="field">
          <label for="p-active">Se puede elegir</label>
          <label class="checkbox-line">
            <input type="checkbox" id="p-active" name="active" ${product?.active === false ? "" : "checked"} />
            <span>Aparece al cargar un evento</span>
          </label>
          <p class="hint">Desmarcado, deja de ofrecerse sin borrar nada de lo ya cargado.</p>
        </div>
      </div>

      <div class="label" style="margin-top:14px">Campos propios de este producto</div>
      <p class="hint" style="margin:2px 0 8px">
        Lo que haga falta anotar sólo en este producto, y aparece en el formulario del evento.
      </p>
      <div id="schema-rows">${schema.map((f) => schemaRowHtml(f)).join("")}</div>
      <button type="button" class="icon-btn" id="add-field">+ Agregar campo</button>

      <p class="error-text" id="product-error" hidden></p>
      <div class="modal-actions">
        <button type="button" class="btn secondary" data-close>Cancelar</button>
        <button type="submit" class="btn">Guardar</button>
      </div>
    </form>
  `);

  modal.querySelectorAll("[data-close]").forEach((btn) => btn.addEventListener("click", () => modal.close()));

  const form = modal.querySelector("#product-form");
  const errorEl = modal.querySelector("#product-error");
  const rows = modal.querySelector("#schema-rows");

  modal.querySelector("#add-field").addEventListener("click", () => {
    rows.insertAdjacentHTML("beforeend", schemaRowHtml());
  });

  rows.addEventListener("click", (e) => {
    if (e.target.dataset?.removeRow !== undefined) e.target.closest("[data-schema-row]").remove();
  });

  // El campo de opciones sólo tiene sentido para las listas.
  rows.addEventListener("change", (e) => {
    if (e.target.dataset?.schemaType === undefined) return;
    const row = e.target.closest("[data-schema-row]");
    row.querySelector("[data-schema-options-field]").hidden = e.target.value !== "select";
  });

  function collectSchema() {
    return [...rows.querySelectorAll("[data-schema-row]")]
      .map((row) => {
        const label = row.querySelector("[data-schema-label]").value.trim();
        if (!label) return null;
        const type = row.querySelector("[data-schema-type]").value;
        const field = {
          name: row.dataset.fieldName || slugifyFieldName(label),
          label,
          type,
        };
        if (type === "select") {
          field.options = row
            .querySelector("[data-schema-options]")
            .value.split(",")
            .map((o) => o.trim())
            .filter(Boolean);
        }
        return field;
      })
      .filter(Boolean);
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    errorEl.hidden = true;

    const schemaValue = collectSchema();
    const names = schemaValue.map((f) => f.name);
    if (new Set(names).size !== names.length) {
      errorEl.textContent = "Hay dos campos propios con la misma etiqueta. Cambiá uno.";
      errorEl.hidden = false;
      return;
    }

    const values = {
      name: form.name.value.trim(),
      color: form.color.value,
      kind: form.kind.value,
      active: form.active.checked,
      form_schema: schemaValue,
    };

    try {
      if (editing) {
        await updateProduct(product.id, values);
      } else {
        await createProduct(values);
      }
      modal.close();
      onSaved?.();
    } catch (error) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
    }
  });
}

/**
 * Un paquete guarda las tarifas típicas de un tipo de evento (los 3-4 packs de
 * cumpleaños, por ejemplo). Al elegirlo en el formulario, las tarifas se
 * precargan y después se pueden ajustar si ese evento fue distinto.
 *
 * Nombre y tarifas van a tablas separadas: el empleado ve el nombre para poder
 * elegir el paquete, pero package_rates es sólo admin.
 */
function openPackageModal(productId, pkg, rates, onSaved) {
  const lines = Array.isArray(rates?.cost_lines) ? rates.cost_lines : [];
  // Sin precio cargado, se ofrecen las tres líneas de siempre con el monto
  // vacío: base para 25, chico extra desde el 26, profesor extra desde el 3ro.
  // Sólo hay que poner números; lo que no aplique se quita.
  const prices =
    Array.isArray(rates?.price_lines) && rates.price_lines.length > 0
      ? rates.price_lines
      : [
          { concept: "Paquete (hasta 25 chicos)", amount: "", per: "fijo" },
          { concept: "Chico extra", amount: "", per: "chico", included: 25 },
          { concept: "Profesor extra", amount: "", per: "profesor", included: 2 },
        ];

  const modal = openModal(`
    <button class="modal-close" type="button" data-close>✕</button>
    <h3>${pkg ? "Editar paquete" : "Nuevo paquete"}</h3>
    <form id="package-form">
      <div class="form-grid">
        <div class="field full">
          <label for="pk-name">Nombre del paquete</label>
          <input type="text" id="pk-name" name="name" required placeholder="Cumpleaños Sirio"
                 value="${escapeHtml(pkg?.name ?? "")}" />
        </div>
        <div class="field full">
          <label for="pk-description">Qué incluye</label>
          <textarea id="pk-description" name="description" rows="2">${escapeHtml(pkg?.description ?? "")}</textarea>
        </div>
        <div class="field">
          <label for="pk-teachers">Profesores por defecto</label>
          <input type="number" step="1" min="0" inputmode="numeric" id="pk-teachers" name="default_teachers_count"
                 value="${rates?.default_teachers_count ?? 0}" />
        </div>
        <div class="field">
          <label for="pk-order">Orden</label>
          <input type="number" step="1" min="0" inputmode="numeric" id="pk-order" name="sort_order"
                 value="${pkg?.sort_order ?? 0}" />
        </div>
      </div>

      <div class="label" style="margin-top:14px">Precio</div>
      <p class="hint" style="margin:2px 0 8px">
        Completá los montos. "A partir de" es desde qué cantidad se cobra ese extra.
        Lo que no uses, quitalo con la ✕.
      </p>
      <div id="price-lines">${prices.map((l) => costLineRowHtml(l, "price")).join("")}</div>
      <button type="button" class="icon-btn" id="add-price-line">+ Agregar línea de precio</button>

      <div class="label" style="margin-top:18px">Costos</div>
      <p class="hint" style="margin:2px 0 8px">
        Si un costo lo cotizan por evento (el club de Belgrano), marcalo
        <strong>presupuesto por evento</strong> y dejá el monto vacío: se pide al cargar cada cumpleaños.
      </p>
      <div id="cost-lines">${lines.map((l) => costLineRowHtml(l, "cost", "package")).join("")}</div>
      <button type="button" class="icon-btn" id="add-cost-line">+ Agregar costo</button>

      <table class="scenario-table" style="margin-top:14px">
        <thead><tr><th></th><th>25 chicos</th><th>35 chicos</th></tr></thead>
        <tbody>
          <tr><th>Precio</th><td id="sc-p25"></td><td id="sc-p35"></td></tr>
          <tr><th>Costo</th><td id="sc-c25"></td><td id="sc-c35"></td></tr>
          <tr><th>Ganancia</th><td id="sc-g25"></td><td id="sc-g35"></td></tr>
        </tbody>
      </table>

      <p class="error-text" id="package-error" hidden></p>
      <div class="modal-actions">
        ${pkg ? '<button type="button" class="btn danger" id="delete-package">Borrar</button>' : ""}
        <button type="button" class="btn secondary" data-close>Cancelar</button>
        <button type="submit" class="btn">Guardar</button>
      </div>
    </form>
  `);

  modal.querySelectorAll("[data-close]").forEach((btn) => btn.addEventListener("click", () => modal.close()));

  modal.querySelector("#delete-package")?.addEventListener("click", async () => {
    if (!confirmDialog("¿Borrar este paquete? Los eventos que lo usaron no se tocan.")) return;
    await deletePackage(pkg.id);
    modal.close();
    onSaved?.();
  });

  const form = modal.querySelector("#package-form");
  const errorEl = modal.querySelector("#package-error");
  const costWrap = modal.querySelector("#cost-lines");
  const priceWrap = modal.querySelector("#price-lines");

  // Dos escenarios lado a lado: a 25 (lo que cubre el base) y a 35 (lo que
  // pasa de verdad). Es el momento de ver si el chico extra está bien cobrado.
  function refresh() {
    const teachers = Number(form.default_teachers_count.value) || 0;
    const costs = readCostLines(costWrap);
    const priceLines = readCostLines(priceWrap);

    for (const wrap of [costWrap, priceWrap]) {
      for (const row of wrap.querySelectorAll("[data-cost-line]")) {
        const amount = parseAmount(row.querySelector(".line-amount").value);
        const per = row.querySelector(".line-per").value;
        const included = Number(row.querySelector(".line-included")?.value) || 0;
        const quoteBox = row.querySelector(".line-quote");
        const quote = quoteBox ? quoteBox.checked : row.dataset.quote === "1";
        row.classList.toggle("needs-quote", Boolean(quote) && !(amount > 0));
        const incField = row.querySelector(".line-included-field");
        if (incField) incField.hidden = per === "fijo";
        const counts = { children_count: 25, teachers_count: teachers, souvenirs_count: 25 };
        const veces = Math.max((per === "chico" || per === "souvenir" ? 25 : per === "profesor" ? teachers : 1) - (per === "fijo" ? 0 : included), 0);
        row.querySelector("[data-line-total]").textContent = amount > 0 ? formatCurrency(amount * veces) : "—";
      }
    }

    for (const chicos of [25, 35]) {
      const counts = { children_count: chicos, teachers_count: teachers, souvenirs_count: chicos };
      const precio = linesTotal(priceLines, counts);
      const costo = linesTotal(costs, counts);
      const ganancia = precio - costo;
      const margen = precio > 0 ? Math.round((ganancia / precio) * 100) : null;
      modal.querySelector(`#sc-p${chicos}`).textContent = formatCurrency(precio);
      modal.querySelector(`#sc-c${chicos}`).textContent = formatCurrency(costo);
      const g = modal.querySelector(`#sc-g${chicos}`);
      g.textContent = `${formatCurrency(ganancia)}${margen === null ? "" : ` · ${margen}%`}`;
      g.className = ganancia >= 0 ? "positive" : "negative";
    }
  }

  form.addEventListener("input", refresh);
  form.addEventListener("change", refresh);

  modal.querySelector("#add-cost-line").addEventListener("click", () => {
    costWrap.insertAdjacentHTML("beforeend", costLineRowHtml(undefined, "cost", "package"));
    formatAmountsIn(costWrap);
    refresh();
  });
  modal.querySelector("#add-price-line").addEventListener("click", () => {
    priceWrap.insertAdjacentHTML("beforeend", costLineRowHtml(undefined, "price"));
    formatAmountsIn(priceWrap);
    refresh();
  });
  for (const wrap of [costWrap, priceWrap]) {
    wrap.addEventListener("click", (e) => {
      if (e.target.classList.contains("remove-line")) {
        e.target.closest("[data-cost-line]").remove();
        refresh();
      }
    });
  }

  wireAmountInputs(modal);
  refresh();

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    errorEl.hidden = true;

    try {
      await savePackage(
        {
          product_id: productId,
          name: form.name.value.trim(),
          description: form.description.value.trim() || null,
          sort_order: Number(form.sort_order.value) || 0,
        },
        {
          default_teachers_count: Number(form.default_teachers_count.value) || 0,
          cost_lines: readCostLines(costWrap),
          price_lines: readCostLines(priceWrap),
        },
        pkg?.id ?? null
      );
      modal.close();
      onSaved?.();
    } catch (error) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
    }
  });
}
